import { HIVER_SERVICE_SECTION_NO } from '@/constants/product'

/**
 * 하이버 해외배송 셀러 현지 발주 확인 (261021-해외배송-통관타임라인 Task 9, 기획 S-1).
 *
 * 배송준비 = 현지 발주 후 매입·검수. 하이버 해외배송(OS) 주문을 상품준비 → 배송준비로 넘길 때
 * 셀러가 "현지 발주를 완료했어요" 를 체크해야 하고, 체크 값(`overseasOrderConfirmed: true`)을
 * order-api → api-cooperate 로 보낸다. api-cooperate 는 값이 없으면 해외배송 건을 DA00005 로 거절한다.
 * 배송중 → 배송준비 되돌림은 대상이 아니다(서버도 상품준비(4) → 배송준비(10) 만 본다).
 */

/** 주문 라인 배송구분 — 해외배송 (order-api `dlvyTypeCd`, DB TB_C_ORDER_DETAIL_PRODUCT.DLVY_TYPE_CD) */
export const OVERSEAS_DELIVERY_TYPE = 'OS'

/** 상품준비 주문상태 코드 */
const PRODUCT_PREPARE_STATUS_CD = '4'

export const OVERSEAS_CONFIRM_TITLE = '해외배송 주문은 현지 발주 후 배송준비로 처리해 주세요'

export interface OverseasJudgeTarget {
  orderDetailCd: string
  orderSttusCd?: string
  dlvyTypeCd?: string | null
  srvcSeNo?: number | null
}

/**
 * 현지 발주 확인이 필요한 주문인지 — 하이버 + 해외배송 + 상품준비.
 * 서비스 번호는 행 값(order-api `srvcSeNo`)을 우선하고, 없으면 현재 채널 서비스 번호로 본다.
 */
export function needsOverseasConfirm(order: OverseasJudgeTarget, fallbackServiceNo?: number | null): boolean {
  if (!order || order.dlvyTypeCd !== OVERSEAS_DELIVERY_TYPE) return false
  if (order.orderSttusCd !== undefined && order.orderSttusCd !== PRODUCT_PREPARE_STATUS_CD) return false
  const serviceNo = order.srvcSeNo ?? fallbackServiceNo
  return Number(serviceNo) === HIVER_SERVICE_SECTION_NO
}

/** 선택한 주문 중 현지 발주 확인이 필요한 주문 (목록 순서 유지) */
export function pickOverseasOrders<T extends OverseasJudgeTarget>(
  orders: T[],
  selectedIds: string[],
  fallbackServiceNo?: number | null
): T[] {
  const selected = new Set(selectedIds)
  return (orders || []).filter((order) => selected.has(order.orderDetailCd) && needsOverseasConfirm(order, fallbackServiceNo))
}

/** 체크 문구 */
export function overseasConfirmCheckLabel(overseasCount: number): string {
  return `해외배송 주문 ${overseasCount}건의 현지 발주를 완료했어요`
}

/** 선택 요약 문구 */
export function overseasSelectionSummary(selectedCount: number, overseasCount: number): string {
  return `선택한 ${selectedCount}건 중 해외배송 ${overseasCount}건`
}

/** 배송준비 요청 바디 — 확인했을 때만 `overseasOrderConfirmed: true` 를 싣는다 */
export function buildDeliveryReadyBody<T>(data: T[], overseasOrderConfirmed?: boolean): { data: T[]; overseasOrderConfirmed?: true } {
  return overseasOrderConfirmed === true ? { data, overseasOrderConfirmed: true } : { data }
}
